import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completionKeymap,
} from "@codemirror/autocomplete"
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands"
import { cpp } from "@codemirror/lang-cpp"
import { css } from "@codemirror/lang-css"
import { go } from "@codemirror/lang-go"
import { html } from "@codemirror/lang-html"
import { java } from "@codemirror/lang-java"
import { javascript } from "@codemirror/lang-javascript"
import { json } from "@codemirror/lang-json"
import { markdown } from "@codemirror/lang-markdown"
import { php } from "@codemirror/lang-php"
import { python } from "@codemirror/lang-python"
import { rust } from "@codemirror/lang-rust"
import { sql } from "@codemirror/lang-sql"
import { yaml } from "@codemirror/lang-yaml"
import {
  bracketMatching,
  foldKeymap,
  indentOnInput,
} from "@codemirror/language"
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search"
import { Compartment, EditorState } from "@codemirror/state"
import {
  keymap as cmKeymap,
  crosshairCursor,
  drawSelection,
  dropCursor,
  EditorView,
  highlightActiveLine,
  highlightSpecialChars,
  rectangularSelection,
} from "@codemirror/view"
import { TextSelection } from "@tiptap/pm/state"
import { fikaTheme } from "./codeMirrorFikaTheme.js"

export class CodeMirrorView {
  node: any
  view: any
  getPos: any
  cm: EditorView
  updating: boolean
  onChange: () => void
  language: string
  languageCompartment: Compartment

  constructor(
    node: any,
    view: any,
    getPos: any,
    parent: HTMLElement,
    onChange: () => void,
    language?: string,
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos
    this.onChange = onChange
    this.language = language || node.attrs?.language || "text"
    this.languageCompartment = new Compartment()

    this.cm = new EditorView({
      doc: this.node.textContent || "",
      extensions: [
        [
          highlightActiveLine(),
          highlightSpecialChars(),
          history(),
          drawSelection(),
          dropCursor(),
          EditorState.allowMultipleSelections.of(true),
          indentOnInput(),
          bracketMatching(),
          closeBrackets(),
          autocompletion(),
          rectangularSelection(),
          crosshairCursor(),
          highlightSelectionMatches(),
          EditorView.lineWrapping,
          cmKeymap.of([
            ...closeBracketsKeymap,
            ...defaultKeymap,
            ...searchKeymap,
            ...historyKeymap,
            ...foldKeymap,
            ...completionKeymap,
            indentWithTab,
          ]),
        ],
        this.languageCompartment.of(getLanguageMode(this.language)),
        cmKeymap.of([...this.codeMirrorKeymap()]),
        EditorView.updateListener.of(update => this.forwardUpdate(update)),
        fikaTheme,
      ],
      parent,
    })

    this.updating = false
  }

  forwardUpdate(update: any) {
    if (this.updating || !this.cm.hasFocus) return
    let offset = this.getPos() + 1
    const { main } = update.state.selection
    const selFrom = offset + main.from
    const selTo = offset + main.to
    const pmSel = this.view.state.selection
    if (update.docChanged || pmSel.from !== selFrom || pmSel.to !== selTo) {
      const tr = this.view.state.tr
      update.changes.iterChanges(
        (fromA: number, toA: number, fromB: number, toB: number, text: any) => {
          if (text.length) {
            tr.replaceWith(
              offset + fromA,
              offset + toA,
              this.view.state.schema.text(text.toString()),
            )
          } else {
            tr.delete(offset + fromA, offset + toA)
          }
          offset += toB - fromB - (toA - fromA)
        },
      )

      tr.setSelection(TextSelection.create(tr.doc, selFrom, selTo))
      this.view.dispatch(tr)

      if (update.docChanged) {
        this.onChange()
      }
    }
  }

  setSelection(anchor: number, head: number) {
    this.cm.focus()
    this.updating = true
    this.cm.dispatch({ selection: { anchor, head } })
    this.updating = false
  }

  codeMirrorKeymap() {
    const view = this.view
    return [
      { key: "ArrowUp", run: () => this.maybeEscape("line", -1) },
      { key: "ArrowLeft", run: () => this.maybeEscape("char", -1) },
      { key: "ArrowDown", run: () => this.maybeEscape("line", 1) },
      { key: "ArrowRight", run: () => this.maybeEscape("char", 1) },
      {
        key: "Backspace",
        run: () => this.handleBackspace(),
      },
      {
        key: "Delete",
        run: () => this.handleDelete(),
      },
      {
        key: "Ctrl-Enter",
        run: () => {
          view.focus()
          return true
        },
      },
      {
        key: "Escape",
        run: () => {
          view.focus()
          return true
        },
      },
    ]
  }

  maybeEscape(unit: string, dir: number) {
    const { state } = this.cm
    const { main } = state.selection
    if (!main.empty) return false
    const lineMain = unit === "line" ? state.doc.lineAt(main.head) : main
    if (dir < 0 ? lineMain.from > 0 : lineMain.to < state.doc.length)
      return false
    const targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize)
    const selection = this.view.state.selection.constructor.near(
      this.view.state.doc.resolve(targetPos),
      dir,
    )
    const tr = this.view.state.tr.setSelection(selection).scrollIntoView()
    this.view.dispatch(tr)
    this.view.focus()
    return true
  }

  handleBackspace() {
    const { state } = this.cm
    const content = state.doc.toString()

    if (!content.trim()) {
      const pos = this.getPos()
      const tr = this.view.state.tr.delete(pos, pos + this.node.nodeSize)
      this.view.dispatch(tr)
      this.view.focus()
      return true
    }

    return false
  }

  handleDelete() {
    const { state } = this.cm
    const content = state.doc.toString()

    if (!content.trim()) {
      const pos = this.getPos()
      const tr = this.view.state.tr.delete(pos, pos + this.node.nodeSize)
      this.view.dispatch(tr)
      this.view.focus()
      return true
    }

    return false
  }

  update(node: any) {
    if (node.type !== this.node.type) return false

    this.node = node
    if (this.updating) return true

    const newLanguage = node.attrs?.language || "text"
    if (newLanguage !== this.language) {
      this.language = newLanguage
      const newLanguageMode = getLanguageMode(this.language)
      this.cm.dispatch({
        effects: this.languageCompartment.reconfigure(newLanguageMode),
      })
    }

    const newText = node.textContent || ""
    const curText = this.cm.state.doc.toString()

    if (newText !== curText) {
      let start = 0
      let curEnd = curText.length
      let newEnd = newText.length

      while (
        start < curEnd &&
        curText.charCodeAt(start) === newText.charCodeAt(start)
      ) {
        ++start
      }

      while (
        curEnd > start &&
        newEnd > start &&
        curText.charCodeAt(curEnd - 1) === newText.charCodeAt(newEnd - 1)
      ) {
        curEnd--
        newEnd--
      }

      this.updating = true
      this.cm.dispatch({
        changes: {
          from: start,
          to: curEnd,
          insert: newText.slice(start, newEnd),
        },
      })
      this.updating = false
    }

    return true
  }

  selectNode() {
    this.cm.focus()
  }

  stopEvent() {
    return true
  }

  destroy() {
    if (this.cm) {
      this.cm.destroy()
    }
  }
}

function getLanguageMode(language: string) {
  switch (language?.toLowerCase()) {
    case "javascript":
    case "js":
      return javascript()
    case "jsx":
      return javascript({ jsx: true })
    case "typescript":
    case "ts":
      return javascript({ typescript: true })
    case "tsx":
      return javascript({ typescript: true, jsx: true })
    case "rust":
    case "rs":
      return rust()
    case "c":
    case "cpp":
    case "c++":
      return cpp()
    case "go":
    case "golang":
      return go()
    case "java":
      return java()
    case "php":
      return php()
    case "sql":
      return sql()
    case "yaml":
    case "yml":
      return yaml()
    case "html":
      return html()
    case "css":
      return css()
    case "json":
      return json()
    case "markdown":
    case "md":
      return markdown()
    case "python":
    case "py":
      return python()
    default:
      return html() // fallback to HTML for unknown languages
  }
}
